// DIAGNOSTIC FRICTION D'ÉQUIPE - WEDO Coaching
// 15 questions, 5 dimensions, échelle 1-5

export const FRICTION_DIMENSIONS = {
  clarity: {
    id: 'clarity',
    name: 'Clarté et priorités',
    shortName: 'Priorités',
    description: 'Niveau de compréhension et d\'alignement sur les priorités',
    color: '#007BFF'
  },
  decision: {
    id: 'decision',
    name: 'Décision et fonctionnement',
    shortName: 'Décision',
    description: 'Rapidité et application des décisions',
    color: '#8E44AD'
  },
  accountability: {
    id: 'accountability',
    name: 'Responsabilisation',
    shortName: 'Responsabilisation',
    description: 'Autonomie et clarté des responsabilités',
    color: '#FF8C00'
  },
  communication: {
    id: 'communication',
    name: 'Communication',
    shortName: 'Communication',
    description: 'Qualité des échanges et gestion des désaccords',
    color: '#52a385'
  },
  leader: {
    id: 'leader',
    name: 'Posture du leader',
    shortName: 'Posture',
    description: 'Positionnement du manager par rapport à l\'équipe',
    color: '#e62c86'
  }
};

export const FRICTION_CATEGORIES = {
  fluid: {
    min: 0,
    max: 25,
    name: 'Organisation fluide',
    color: '#52a385',
    description: 'Votre organisation fonctionne de manière fluide avec des mécanismes de décision et d\'exécution efficaces.',
    analysis: 'Votre équipe présente un niveau de friction très faible. Les mécanismes de décision, de responsabilisation et d\'exécution fonctionnent de manière fluide. C\'est rare et cela constitue un avantage compétitif significatif.'
  },
  latent: {
    min: 25,
    max: 50,
    name: 'Tensions latentes',
    color: '#f3a114',
    description: 'Des tensions existent mais restent gérables. Certains mécanismes pourraient être optimisés.',
    analysis: 'Votre organisation présente des tensions latentes. Dans les équipes que j\'accompagne, ce niveau se traduit souvent par des décisions qui prennent plus de temps que nécessaire et des sujets qui remontent systématiquement au manager.'
  },
  structural: {
    min: 50,
    max: 75,
    name: 'Friction structurelle',
    color: '#FF8C00',
    description: 'Des frictions significatives impactent l\'efficacité collective. Une intervention est recommandée.',
    analysis: 'Votre organisation présente un niveau de friction modéré à élevé. Dans les équipes que j\'accompagne, ce niveau se traduit souvent par : des décisions qui reviennent au dirigeant, des projets qui avancent plus lentement, des tensions peu exprimées, une surcharge progressive du manager.'
  },
  high: {
    min: 75,
    max: 100,
    name: 'Friction opérationnelle élevée',
    color: '#DC143C',
    description: 'Les frictions sont importantes et impactent significativement la performance. Une action rapide est nécessaire.',
    analysis: 'Votre organisation présente un niveau de friction élevé. Cela se traduit généralement par une surcharge importante du dirigeant, des projets qui stagnent, des tensions non résolues et une dépendance excessive au manager pour toutes les décisions.'
  }
};

export const QUESTIONS = {
  fr: [
    // DIMENSION 1: Clarté et priorités (Questions 1-3)
    {
      id: 1,
      question: "Les priorités de l'équipe sont clairement comprises par tous.",
      dimension: "clarity",
      reverse: false
    },
    {
      id: 2,
      question: "Les membres de l'équipe savent sur quels sujets concentrer leurs efforts.",
      dimension: "clarity",
      reverse: false
    },
    {
      id: 3,
      question: "Lorsque les priorités changent, les raisons sont expliquées et comprises.",
      dimension: "clarity",
      reverse: false
    },
    
    // DIMENSION 2: Décision et fonctionnement (Questions 4-6)
    {
      id: 4,
      question: "Les décisions importantes sont prises rapidement lorsque c'est nécessaire.",
      dimension: "decision",
      reverse: false
    },
    {
      id: 5,
      question: "Une fois prises, les décisions sont réellement appliquées.",
      dimension: "decision",
      reverse: false
    },
    {
      id: 6,
      question: "Les sujets ne remontent pas systématiquement au manager ou au dirigeant.",
      dimension: "decision",
      reverse: false
    },
    
    // DIMENSION 3: Responsabilisation (Questions 7-9)
    {
      id: 7,
      question: "Les responsabilités sont clairement définies dans l'équipe.",
      dimension: "accountability",
      reverse: false
    },
    {
      id: 8,
      question: "Les membres de l'équipe prennent des initiatives sur leurs sujets.",
      dimension: "accountability",
      reverse: false
    },
    {
      id: 9,
      question: "Les projets avancent sans supervision permanente du manager.",
      dimension: "accountability",
      reverse: false
    },
    
    // DIMENSION 4: Communication (Questions 10-12)
    {
      id: 10,
      question: "Les désaccords peuvent être exprimés ouvertement dans l'équipe.",
      dimension: "communication",
      reverse: false
    },
    {
      id: 11,
      question: "Les problèmes sont remontés suffisamment tôt pour être traités.",
      dimension: "communication",
      reverse: false
    },
    {
      id: 12,
      question: "Les réunions permettent d'aborder les sujets importants sans les éviter.",
      dimension: "communication",
      reverse: false
    },
    
    // DIMENSION 5: Posture du leader (Questions 13-15)
    {
      id: 13,
      question: "Je dois intervenir régulièrement pour débloquer certaines situations.",
      dimension: "leader",
      reverse: true  // Score inversé: d'accord = friction
    },
    {
      id: 14,
      question: "Certaines décisions pourraient être prises par l'équipe sans passer par moi.",
      dimension: "leader",
      reverse: true  // Score inversé: d'accord = friction
    },
    {
      id: 15,
      question: "Je dispose du recul nécessaire pour piloter l'équipe plutôt que gérer les urgences.",
      dimension: "leader",
      reverse: false  // Score normal: d'accord = fluide
    }
  ]
};

export const ANSWER_SCALE = [
  { value: 1, label: 'Pas du tout d\'accord' },
  { value: 2, label: 'Plutôt pas d\'accord' },
  { value: 3, label: 'Neutre' },
  { value: 4, label: 'Plutôt d\'accord' },
  { value: 5, label: 'Tout à fait d\'accord' }
];

export const TRANSLATIONS = {
  fr: {
    // Landing Page
    landingTitle: "Les Frictions Invisibles d'une Équipe",
    landingSubtitle: "En 3 minutes, identifiez les mécanismes qui ralentissent votre équipe ou votre organisation.",
    startDiagnostic: "Démarrer le diagnostic",
    
    // Intro Page
    introTitle: "Diagnostic d'équipe",
    continueButton: "Continuer",
    
    // Personal Info
    personalInfoTitle: "Vos coordonnées",
    personalInfoSubtitle: "Pour recevoir votre analyse personnalisée",
    firstName: "Prénom",
    lastName: "Nom",
    email: "Email professionnel",
    phone: "Téléphone (optionnel)",
    submit: "Accéder au diagnostic",
    
    // Questions
    questionProgress: "Question",
    of: "/",
    previous: "Précédent",
    next: "Suivant",
    seeResults: "Voir mes résultats",
    
    // Results Page
    resultsTitle: "Votre indice de friction",
    frictionIndex: "Indice de friction",
    dimensionAnalysis: "Analyse par dimension",
    riskZone: "Zone de risque organisationnelle",
    riskZones: {
      stable: "Zone stable",
      tension: "Zone de tension",
      critical: "Zone critique"
    },
    
    // Strategic Analysis
    analysisTitle: "Analyse stratégique",
    keyInsight: "Dans la plupart des organisations, les causes réelles de ces frictions ne sont pas celles que l'on imagine au départ.",
    
    // CTA Debrief
    debriefTitle: "Recevoir votre débrief stratégique",
    debriefText: "Je vous propose un échange de 30 minutes pour analyser ce diagnostic.",
    debriefPoints: [
      "ce que révèle réellement votre score",
      "les mécanismes invisibles dans la dynamique de votre équipe",
      "les leviers pour fluidifier la prise de décision et l'exécution collective"
    ],
    debriefDuration: "30 minutes",
    debriefCTA: "Réserver mon débrief stratégique",
    
    // Features
    features: {
      quick: "Rapide",
      quickDesc: "15 questions ciblées en 3 minutes",
      scientific: "Opérationnel",
      scientificDesc: "Focus sur les mécanismes d\'exécution",
      personalized: "Stratégique",
      personalizedDesc: "Analyse orientée leadership"
    }
  }
};

// Fonction de calcul du score de friction avec gestion des questions inversées
export const calculateFrictionScore = (answers, questions) => {
  let totalScore = 0;
  
  // Si questions n'est pas fourni, utiliser le calcul simple
  if (!questions) {
    totalScore = Object.values(answers).reduce((sum, val) => sum + val, 0);
  } else {
    // Calculer avec gestion des scores inversés
    Object.entries(answers).forEach(([index, value]) => {
      const question = questions[parseInt(index)];
      if (question && question.reverse) {
        // Inverser le score: 5 devient 1, 4 devient 2, etc.
        totalScore += (6 - value);
      } else {
        totalScore += value;
      }
    });
  }
  
  // Score minimum = 15 (15 questions x 1)
  // Score maximum = 75 (15 questions x 5)
  const fluidityScore = ((totalScore - 15) / 60) * 100;
  const frictionIndex = 100 - fluidityScore;
  
  return {
    totalScore,
    fluidityScore: Math.round(fluidityScore * 10) / 10,
    frictionIndex: Math.round(frictionIndex * 10) / 10
  };
};

// Fonction de calcul des scores par dimension avec gestion des questions inversées
export const calculateDimensionScores = (answers, questions) => {
  const dimensionScores = {};
  
  Object.keys(FRICTION_DIMENSIONS).forEach(dimension => {
    const dimensionQuestions = questions.filter(q => q.dimension === dimension);
    let dimensionTotal = 0;
    
    dimensionQuestions.forEach(q => {
      const questionIndex = questions.findIndex(quest => quest.id === q.id);
      if (answers[questionIndex] !== undefined) {
        let value = answers[questionIndex];
        // Inverser si nécessaire
        if (q.reverse) {
          value = 6 - value;
        }
        dimensionTotal += value;
      }
    });
    
    // Dimension score: min 3 (3 questions x 1), max 15 (3 questions x 5)
    const dimensionFluidityScore = ((dimensionTotal - 3) / 12) * 100;
    const dimensionFrictionScore = 100 - dimensionFluidityScore;
    
    dimensionScores[dimension] = {
      total: dimensionTotal,
      fluidityScore: Math.round(dimensionFluidityScore * 10) / 10,
      frictionScore: Math.round(dimensionFrictionScore * 10) / 10
    };
  });
  
  return dimensionScores;
};

// Fonction pour déterminer la catégorie de friction
export const getFrictionCategory = (frictionIndex) => {
  if (frictionIndex < 25) return FRICTION_CATEGORIES.fluid;
  if (frictionIndex < 50) return FRICTION_CATEGORIES.latent;
  if (frictionIndex < 75) return FRICTION_CATEGORIES.structural;
  return FRICTION_CATEGORIES.high;
};

// Export pour compatibilité avec l'ancien code
export const MANAGEMENT_TYPES = FRICTION_DIMENSIONS;
